import { stardust } from '@eidellev/adonis-stardust/client';
import algoliasearch from 'algoliasearch/lite';
import { autocomplete, getAlgoliaResults } from '@algolia/autocomplete-js';
import { createLocalStorageRecentSearchesPlugin } from '@algolia/autocomplete-plugin-recent-searches';
import i18next from 'i18next';
import i18nextInstance from './i18next';

import { getIndexName } from '../../../app/Libraries/Algolia/AlgoliaUtils';
import {
  getSearchRedirect, isGenericWatchQuery, localePrefix, WATCH_BOOST_FILTER,
} from '../../../app/Libraries/Search/SearchRedirects';
import { FAMILY_PUSH_BANNER_ID_MAP } from '../../../app/Constants/Rolex';
import { getPageLang, getSizedShopifyImageUrl } from './utils';

const searchClient = algoliasearch(
  process.env.ALGOLIA_APP_ID as string,
  process.env.ALGOLIA_SEARCH_KEY as string,
);

/** Generate the URL for a given search query */
const genSearchUrl = (query: string) => {
  const url = stardust.route(
    `${getPageLang()}.search.index`,
    undefined,
    { qs: {
      q: query,
    } },
  );
  return url;
};

/** TAG Heuer terms: any related query gets the brand-page suggestion first —
 *  the products themselves are deliberately not sold online (inquiry-only) */
const TAG_HEUER_TERMS = ['tag heuer', 'tagheuer', 'heuer', 'aquaracer', 'carrera', 'formula 1', 'monaco'];
const isTagHeuerQuery = (query: string): boolean => {
  const q = query.trim().toLowerCase();
  if (q.length < 3) return false;
  // Whole-word match (shared with the server) — "vintage"/"heritage" used to hit "tag".
  // Prefix match keeps the suggestion visible while typing ("carr" → Carrera).
  return getSearchRedirect(q)?.kind === 'tagHeuer' || TAG_HEUER_TERMS.some((term) => term.startsWith(q));
};

/** Proper Rolex family names keyed by URL slug ("gmt-master-ii" → "GMT-Master II") */
const ROLEX_FAMILY_NAMES: Record<string, string> = Object.fromEntries(
  Object.keys(FAMILY_PUSH_BANNER_ID_MAP).map((name) => [name.toLowerCase().replace(/\s+/g, '-'), name]),
);

/** Label for the single redirect suggestion that replaces product results */
const redirectLabel = (path: string): string => {
  const t = (key: string) => i18next.t(key);
  if (path === '/rolex/discover') return t('algolia:autocomplete.discoverRolex');
  if (path === '/rolex/watches') return t('algolia:autocomplete.rolexWatches');
  const family = ROLEX_FAMILY_NAMES[path.replace('/rolex/', '')];
  if (family) return `Rolex ${family}`;
  return t('algolia:autocomplete.allTimepieces');
};

/** Generate the URL for a given product handle */
const genProductUrl = (handle: string) => {
  const url = stardust.route(
    `${getPageLang()}.shopify.slug`,
    { slug: handle },
  );
  return url;
};

document.addEventListener('DOMContentLoaded', async () => {
  // Load i18next
  const t = await i18nextInstance;
  await i18next.loadNamespaces(['algolia']);

  const recentSearchesPlugin = createLocalStorageRecentSearchesPlugin({
    key: 'RECENT_SEARCH',
    limit: 3,
    transformSource({ source }) {
      return {
        ...source,
        getItemUrl({ item }) {
          const url = genSearchUrl(item.id);
          return url;
        },
        templates: {
          ...source.templates,
          item(params) {
            const { item, html } = params;

            return html`
            <a class="aa-ItemLink" href="${genSearchUrl(item.id)}">
              ${/* TODO: For some reason it uses ReverseHighlight by default and I can't find a good way to change it...
                components.Highlight({
                  hit: item,
                  attribute: 'id',
                })*/
                // @ts-ignore: This is just from the docs...
                source.templates.item(params).props.children}
            </a>`;
          },
        },
      };
    },
    translations: {
      removeSearchTitle: t('algolia:autocomplete.removeSearch'),
      fillQueryTitle: (query: string) => t('algolia:autocomplete.fillQuery', { query }),
    },
  });

  autocomplete({
    container: '#autocomplete',
    panelContainer: '#autocomplete-panel',
    panelPlacement: 'full-width',
    placeholder: t('algolia:autocomplete.placeholder'),
    plugins: [recentSearchesPlugin],
    // Never use detached mode (https://www.algolia.com/doc/ui-libraries/autocomplete/core-concepts/detached-mode)
    detachedMediaQuery: 'none',
    insights: true,
    // Hide the pre-search default panel (common searches / featured / quick
    // links) as soon as the visitor types; bring it back when cleared
    onStateChange({ state }) {
      const defaultPanel = document.querySelector<HTMLElement>('.js-search-default');
      if (defaultPanel) defaultPanel.classList.toggle('nav__s-default--hidden', !!state.query);
    },
    // debug: true, // keep the results list open
    getSources({ query }) {
      const tagHeuerUrl = `${getPageLang() === 'fr' ? '/fr' : ''}/collections/timepieces/tag-heuer`;
      // Rolex (guidelines: never product results) and brands we don't carry get ONE
      // link to the right page instead of product suggestions
      const redirect = getSearchRedirect(query);
      const replacesProducts = !!redirect && redirect.kind !== 'tagHeuer';
      const redirectUrl = redirect ? `${localePrefix(getPageLang())}${redirect.path}` : '';
      return [
        {
          sourceId: 'searchRedirect',
          getItems() {
            return replacesProducts ? [{ label: redirectLabel(redirect!.path) }] : [];
          },
          getItemUrl() {
            return redirectUrl;
          },
          templates: {
            item({ item, html }) {
              return html`
                <a class="aa-ItemLink aa-RedirectSuggestion" href="${redirectUrl}">
                  <span class="aa-RedirectSuggestion-label">${item.label}</span>
                  <span class="aa-RedirectSuggestion-arrow" aria-hidden="true">→</span>
                </a>`;
            },
          },
        },
        {
          // TAG Heuer brand suggestion — always first for related queries.
          // The watches are inquiry-only (never in the index); this is the
          // discoverable path to the brand page.
          sourceId: 'tagHeuer',
          getItems() {
            return isTagHeuerQuery(query) ? [{ label: 'TAG Heuer Watches' }] : [];
          },
          getItemUrl() {
            return tagHeuerUrl;
          },
          templates: {
            item({ html }) {
              return html`
                <a class="aa-ItemLink aa-TagHeuerSuggestion" href="${tagHeuerUrl}">
                  <img class="aa-TagHeuerSuggestion-logo" src="/img/tag-heuer-shield.svg" alt="TAG Heuer" />
                  <span class="aa-TagHeuerSuggestion-label">TAG Heuer Watches</span>
                </a>`;
            },
          },
        },
        {
          sourceId: 'products',
          getItems() {
            if (replacesProducts) return [];
            return getAlgoliaResults({
              searchClient,
              queries: [
                {
                  // Featured replica — same merchandised order as the search page
                  // and collection grids. The default (primary) index tie-breaks by
                  // updated_at, which surfaces whatever was touched most recently
                  // (e.g. Tissot straps over Tissot watches).
                  indexName: getIndexName(getPageLang(), 'featured', 'asc'),
                  query,
                  params: {
                    hitsPerPage: 12,
                    // Inquiry-only products (TAG Heuer) never surface in
                    // suggestions (brand direction)
                    filters: 'NOT tags:"inquiry-only"',
                    // "watches" etc.: real watches before WOLF rolls/winders
                    ...(isGenericWatchQuery(query) ? { optionalFilters: [WATCH_BOOST_FILTER] } : {}),
                  },
                },
              ],
            });
          },
          templates: {
            item({ item, components, html }) {
              // Product thumbnail (2x for retina), falling back to the search icon
              const image = item.image
                ? getSizedShopifyImageUrl(item.image as string, 96)
                : null;
              // Show the variant SKU under the title when it's what matched the
              // query (e.g. searching "M79360B"), since titles alone can be identical
              const skuHighlight = (item._highlightResult as any)?.sku;
              const skuMatched = item.sku
                && skuHighlight?.matchLevel
                && skuHighlight.matchLevel !== 'none';
              return html`
              <a class="aa-ItemWrapper" href="${genProductUrl(item.handle as string)}">
                <div class="aa-ItemContent">
                  <div class="aa-ItemIcon aa-ItemIcon--alignTop aa-ItemIcon--noBorder aa-ItemIcon--product">
                    ${image
                      ? html`<img src="${image}" alt="${item.title}" width="48" height="48" loading="lazy" />`
                      : html`<svg class="aa-SearchIcon" viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M16.041 15.856c-0.034 0.026-0.067 0.055-0.099 0.087s-0.060 0.064-0.087 0.099c-1.258 1.213-2.969 1.958-4.855 1.958-1.933 0-3.682-0.782-4.95-2.050s-2.050-3.017-2.050-4.95 0.782-3.682 2.050-4.95 3.017-2.050 4.95-2.050 3.682 0.782 4.95 2.050 2.050 3.017 2.050 4.95c0 1.886-0.745 3.597-1.959 4.856zM21.707 20.293l-3.675-3.675c1.231-1.54 1.968-3.493 1.968-5.618 0-2.485-1.008-4.736-2.636-6.364s-3.879-2.636-6.364-2.636-4.736 1.008-6.364 2.636-2.636 3.879-2.636 6.364 1.008 4.736 2.636 6.364 3.879 2.636 6.364 2.636c2.125 0 4.078-0.737 5.618-1.968l3.675 3.675c0.391 0.391 1.024 0.391 1.414 0s0.391-1.024 0-1.414z"></path></svg>`}
                  </div>
                  <div class="aa-ItemContentBody">
                    <div class="aa-ItemContentTitle">
                      ${components.Highlight({ hit: item, attribute: 'title' })}
                    </div>
                    ${skuMatched
                      ? html`<div class="aa-ItemContentDescription aa-ItemContentDescription--sku">
                          ${components.Highlight({ hit: item, attribute: 'sku' })}
                        </div>`
                      : ''}
                  </div>
                  <div class="aa-ItemActions">
                    <button
                      class="aa-ItemActionButton aa-DesktopOnly aa-ActiveOnly"
                      type="button"
                      title="${t('algolia:autocomplete.selectItem')}"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        width="20"
                        height="20"
                        fill="currentColor"
                      >
                        <path d="M18.984 6.984h2.016v6h-15.188l3.609 3.609-1.406 1.406-6-6 6-6 1.406 1.406-3.609 3.609h13.172v-4.031z" />
                      </svg>
                    </button>
                  </div>
                </div>
              </a>`;
            },
            // "Show all results" link at the end of the suggestions
            footer(params: any) {
              const { state, html } = params;
              if (!state?.query) {
                return '';
              }
              return html`
              <a class="aa-ShowAllResults" href="${genSearchUrl(state.query)}">
                ${t('algolia:autocomplete.showAllResults')}
              </a>`;
            },
          },
          getItemUrl({ item }) {
            const url = genProductUrl(item.handle as string);
            return url;
          },
          // Apparently this doesn't fire if you just try and search without selecting an item,
          // so we need to add our own handler
          // onSubmit: (data) => {
          //   console.debug('Algolia onSubmit', data);
          //   // window.location.href = `/search?q=${item.handle}`;
          // },
        },
      ];
    },
  });

  // Add custom event listener for pressing enter on the search box

  // TODO: Is this safe or do I need to wait for the DOM to be ready?
  const input = document.querySelector('#autocomplete .aa-Input');
  input?.addEventListener('keydown', function(event: KeyboardEvent) {
    // console.debug('keydown', event.key);

    // Check if the key pressed was 'Enter'
    if (event.key === 'Enter') {
      // Prevent default so we handle saving as a recent search ourselves
      event.preventDefault();

      const searchInput = event.target as HTMLInputElement;

      // Abort if selecting an item from the dropdown
      const selectedItem = document.querySelector('.aa-Item[aria-selected="true"]');
      if (selectedItem) {
        return;
      }

      // Add it as a recent search (it automatically dedupes + sorts)
      recentSearchesPlugin.data!.addItem({
        id: searchInput.value,
        label: searchInput.value,
      });

      // Rolex / TAG Heuer / not-carried brands go straight to their page
      const redirect = getSearchRedirect(searchInput.value);
      if (redirect) {
        window.location.href = `${localePrefix(getPageLang())}${redirect.path}`;
        return;
      }

      // Navigate to the search page
      const url = genSearchUrl(searchInput.value);
      window.location.href = url;
    }
  });
});
